import { AxiosError, AxiosResponse } from "axios";

/* eslint-disable @typescript-eslint/consistent-type-definitions */

// MISC //

export enum CandidaciesSortKeyAndOrder {
  TimeInStageAsc = "Time in stage (shortest first)",
  TimeInStageDesc = "Time in stage (longest first)",
  ScoreDesc = "Score (highest first)",
}

export type CompanyInformationPayload = {
  logoUrl: string | null;
  address: string | null;
  description: string | null;
  naicsCodes: string[];
  customerIndustries: string[];
  manufacturingCapabilities: string[];
  equipment: string[];
  materials: string[];
  headcount: number | null;
  foundedYear: string | null;
  location: string | null;
  dunsNumber: string | null;
  cageCode: string | null;
};

export type CompanyUser = {
  firstName: string;
  lastName: string;
  email: string;
  isInvited: boolean;
  invitationStatus: UserInvitationStatus | null;
  inviterId: number | null;
};

export type HttpError<T> = AxiosError<T>;
export type HttpResponse<T> = AxiosResponse<T>;

export enum JobsSortKeyAndOrder {
  CreatedAtDesc = "Date posted (recent first)",
  CreatedAtAsc = "Date posted (oldest first)",
  TitleAsc = "Job title (A-Z)",
  TitleDesc = "Job title (Z-A)",
}

type ScreeningAnswer = {
  id: number;
  question: string;
  answer: string | null;
  required: boolean;
};

type ScreeningQuestion = {
  id: number;
  question: string;
  required: boolean;
};

type SearchHighlight = {
  "skills.name"?: string[];
  "certifications.name"?: string[];
  "workExperience.title"?: string[];
  "workExperience.company"?: string[];
  "education.degree"?: string[];
};

export type SearchResult = {
  candidacyStatus: CandidacyStatus | null;
  certifications: Certification[];
  education: Education[];
  email: string;
  endorsementsReceived: Endorsement[];
  industries: Industry[];
  primaryResumeId: number | null;
  profile: Profile;
  resumeFile: ResumeFile | null;
  skills: Skill[];
  summary: string;
  userId: number;
  workExperience: WorkExperience[];
};

export type TransformedCandidateDocument = {
  education: Education[];
  primaryResumeId: number | null;
  professionalSummary: string | null;
  profile: Profile;
  workExperience: WorkExperience[];
  certifications: Certification[];
  email: string;
  userId: number;
  resumeFile: ResumeFile | null;
  skills: Skill[];
  industries: Industry[];
  endorsementsReceived: Endorsement[];
};

// MODELS //

export type Candidacy = {
  id: string;
  jobId: string;
  talentId: number;
  screeningAnswers: { screeningAnswers: ScreeningAnswer[] } | null;
  summary: string | null;
  status: CandidacyStatus;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type CandidacyComment = {
  id: number;
  candidacyId: string;
  userId: number;
  content: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type CandidacyInterest = {
  id: number;
  userId: number;
  candidacyId: string;
  isInterested: boolean;
  note: string;
  createdAt: string;
  updatedAt: string;
};

export type CandidacyScoreChange = {
  id: number;
  candidacyId: string;
  userId: number;
  fromScore: number | null;
  toScore: number;
  createdAt: string;
  updatedAt: string;
};

export type CandidacyStatusChange = {
  id: number;
  candidacyId: string;
  userId: number;
  fromStatus: CandidacyStatus | null;
  toStatus: CandidacyStatus;
  createdAt: string;
  updatedAt: string;
};

export type CandidacySummaryChange = {
  id: number;
  candidacyId: string;
  userId: number;
  toSummary: string;
  createdAt: string;
  updatedAt: string;
};

export type Certification = {
  name: string | null;
  organization: string | null;
  credentialId: string | null;
  credentialUrl: string | null;
  endMonth: string | null;
  endYear: string | null;
  startMonth: string | null;
  startYear: string | null;
  isResumeField: boolean | null;
};

export type Company = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

export type CompanyInformation = {
  id: string;
  companyId: string;
  companyInformation: CompanyInformationPayload;
  createdAt: string;
  updatedAt: string;
};

export type Education = {
  school: string | null;
  degree: string | null;
  location: string | null;
  startMonth: string | null;
  startYear: string | null;
  endMonth: string | null;
  endYear: string | null;
  current: boolean | null;
  isResumeField: boolean | null;
};

type EmploymentStatus = {
  title: string;
};

export type Endorsement = {
  teamworkRating: number;
  technicalExperienceRating: number;
  leadershipRating: number;
  isVisible: boolean | null;
  desiredPosition: string | null;
  createdAt: string;
};

export type Industry = {
  name: string | null;
};

export type Job = {
  id: string;
  title: string;
  description: string;
  overview: string;
  responsibilities: string;
  qualifications: string;
  benefits: string | null;
  companyId: string;
  city: string;
  state: string;
  address: string;
  zipCode: string;
  employmentType: EmploymentType;
  shiftType: ShiftType;
  shiftStart: string | null;
  shiftEnd: string | null;
  compensationType: CompensationType;
  compensationMin: number;
  compensationMax: number;
  screeningQuestions: { screeningQuestions: ScreeningQuestion[] } | null;
  status: JobStatus;
  createdAt: string;
  updatedAt: string;
  // TODO: vectorData?
};

export type Profile = {
  desiredPosition: string | null;
  firstName: string | null;
  employmentStatus: EmploymentStatus | null;
  yoeRange: YearsOfExperienceRange | null;
  lastName: string | null;
  location: string | null;
  phone: string;
};

type ResumeFile = {
  id: number | null; // TODO: Is this nullable?
  title: string | null;
  url: string | null;
};

export type Skill = {
  name: string | null;
  isResumeField: boolean | null;
};

export type User = {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  companyId: string;
  role: Role;
  createdAt: string;
  updatedAt: string;
};

export type WorkExperience = {
  title: string | null;
  internalCompany: { name: string; logoUrl?: string | null } | null;
  location: string | null;
  startMonth: string | null;
  startYear: string | null;
  endMonth: string | null;
  endYear: string | null;
  currentPosition: boolean | null;
  descriptions: string[];
  isResumeField: boolean | null;
};

type YearsOfExperienceRange = {
  range: string;
};

export enum CandidacyStatus {
  MATCHED = "MATCHED",
  INTERVIEW = "INTERVIEW",
  OFFER = "OFFER",
  REJECTED = "REJECTED",
}

export enum CompensationType {
  HOURLY = "HOURLY",
  YEARLY = "YEARLY",
}

export enum EmploymentType {
  FULLTIME = "FULLTIME",
  PARTTIME = "PARTTIME",
  CONTRACT = "CONTRACT",
}

export enum JobStatus {
  ACTIVE = "ACTIVE",
  REVIEW = "REVIEW",
  DRAFT = "DRAFT",
  CLOSED = "CLOSED",
}

export enum Role {
  EMPLOYER = "EMPLOYER",
  STAFFER = "STAFFER",
}

export enum ShiftType {
  FIRSTSHIFT = "FIRSTSHIFT",
  SECONDSHIFT = "SECONDSHIFT",
  THIRDSHIFT = "THIRDSHIFT",
  WEEKEND = "WEEKEND",
}

export enum UserInvitationStatus {
  CANCELLED = "CANCELLED",
  EXPIRED = "EXPIRED",
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
}

// PARAMETERS //

export type SearchParameters = {
  jobId: string;
  query: string; // if searching by role
  location: string;
  radius: string; // i.e distance
  yoe: string; // i.e years of experience
  customFilter?: string[]; // i.e skills, backgrounds, etc
  jobStatus?: string;
};

// REQUESTS //

export type CancelUserInvitationRequest = {
  inviteeEmail: string;
};

export type CreateCandidacyInterestRequest = {
  isInterested: boolean;
  note: string;
};

export type CreateCandidacyScoreChangeRequest = {
  toScore: number;
};

export type CreateContactRequest = {
  message: string;
  subject: string;
};

export type CreateOrUpdateCandidacyCommentRequest = {
  content: string;
};

export type CreateOrUpdateCompanyInformationRequest = {
  companyInformation: {
    logoUrl?: string | null; // Can be `undefined` because the logo url is managed separately for now.
    address: string | null;
    description: string | null;
    naicsCodes: string[];
    customerIndustries: string[];
    manufacturingCapabilities: string[];
    equipment: string[];
    materials: string[];
    headcount: number | null;
    foundedYear: string | null;
    location: string | null;
    dunsNumber: string | null;
    cageCode: string | null;
  };
};

export type CreateOrUpdateJobRequest = {
  title: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  employmentType: EmploymentType;
  shiftType: ShiftType;
  shiftStart: string | null;
  shiftEnd: string | null;
  compensationType: CompensationType;
  compensationMin: number;
  compensationMax: number;
  screeningQuestions?: { screeningQuestions: ScreeningQuestion[] } | null; // Can be `undefined` because screening questions are managed in a different section.
  overview: string;
  responsibilities: string;
  qualifications: string;
  benefits: string | null;
  status: JobStatus;
};

export type CreateUserInvitationRequest = {
  firstName: string;
  lastName: string;
  inviteeEmail: string;
};

export type GetJobWithPartialCandidacyCountsByStatusResponse = {
  partialCountsByCandidacyStatus: Record<
    Exclude<CandidacyStatus, typeof CandidacyStatus.REJECTED>,
    number
  >;
  job: Job;
};

export type ResendUserInvitationRequest = {
  inviteeEmail: string;
};

export type ResetPasswordRequest = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export type SearchRequestIntroRequest = {
  jobTitle: string;
  talentId: number;
  talentName: string;
};

export type SetPasswordRequest = {
  password: string;
  confirmPassword: string;
};

export type SignInRequest = {
  email: string;
  password: string;
};

export type UpdateCandidacyRequest = {
  status?: CandidacyStatus;
  summary?: string;
};

export type UpdateUserRequest = {
  firstName?: string;
  lastName?: string;
};

// RESPONSES //

export type CancelUserInvitationResponse = {
  success: true;
};

export type CandidacyWithProfileResponse = {
  candidacy: Candidacy;
  candidacyInterest: boolean | null;
  candidacyScore: number | null;
  candidateProfile: TransformedCandidateDocument;
  timeInStage: string;
};

export type FlatCandidacyWithProfileResponse = {
  // Candidacy portion.
  candidacyId: CandidacyWithProfileResponse["candidacy"]["id"];
  candidacyStatus: CandidacyWithProfileResponse["candidacy"]["status"];
  candidacyTalentId: CandidacyWithProfileResponse["candidacy"]["talentId"];

  // Candidate profile portion.
  candidateProfileDesiredPosition: CandidacyWithProfileResponse["candidateProfile"]["profile"]["desiredPosition"];
  candidateProfileFirstName: CandidacyWithProfileResponse["candidateProfile"]["profile"]["firstName"];
  candidateProfileLastName: CandidacyWithProfileResponse["candidateProfile"]["profile"]["lastName"];
  candidateProfileLocation: CandidacyWithProfileResponse["candidateProfile"]["profile"]["location"];

  // Score portion.
  candidacyScore: CandidacyWithProfileResponse["candidacyScore"];

  // Time in stage portion.
  timeInStage: CandidacyWithProfileResponse["timeInStage"];
};

export type GetCompanyInformationResponse = {
  companyInformation: CompanyInformation | null;
};

export type JobsResponse = {
  job: Job;
  // Field that is computed on the fly on the BE and represents the count
  // of all candidates across all stages except for the rejected stage.
  totalCandidates: number;
};

export type ResendUserInvitationResponse = {
  success: true;
};

export type SearchResponse = {
  hits: {
    hits: {
      _score: number;
      _source: SearchResult;
      highlight: SearchHighlight;
    }[];
    total: { value: number; relation: string };
  };
};

export type SetPasswordResponse = {
  success: true;
};

export type SignInResponse = {
  accessToken: string;
  companies: Company[];
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  refreshToken: string;
};

export type TotalMatchedCandidatesForActiveJobsResponse = {
  totalMatchedCandidates: number;
};

export type VerifyInvitationResponse = {
  success: boolean;
};
